import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/imgs/header/logo.png';
import { getLumivivUrl } from '@/locales/externalLinks';

type FooterLink = {
  label: string;
  to: string;
  external?: boolean;
  disabled?: boolean;
};

type FooterColumnProps = {
  links: FooterLink[];
  title: string;
};

function FooterColumn({ links, title }: FooterColumnProps) {
  return (
    <div className="flex min-w-[106px] shrink-0 flex-col items-start gap-[24px]">
      <h2 className="w-full text-[18px] font-medium leading-[27px] text-[#114861]">{title}</h2>
      <div className="flex w-full flex-col items-start gap-[16px]">
        {links.map((link) => (
          link.disabled ? (
            <span
              key={`${link.to}-${link.label}`}
              aria-disabled="true"
              className="w-full cursor-default text-[16px] font-normal leading-[25.6px] text-[#a3a3a3]"
            >
              {link.label}
            </span>
          ) : link.external ? (
            <a
              key={`${link.to}-${link.label}`}
              href={link.to}
              target="_blank"
              rel="noreferrer"
              className="w-full text-[16px] font-normal leading-[25.6px] text-[#355f72] transition-colors hover:text-[#00a0e9]"
            >
              {link.label}
            </a>
          ) : (
            <Link
              key={`${link.to}-${link.label}`}
              to={link.to}
              className="w-full text-[16px] font-normal leading-[25.6px] text-[#355f72] transition-colors hover:text-[#00a0e9]"
            >
              {link.label}
            </Link>
          )
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  const { t, i18n } = useTranslation();

  const columns: FooterColumnProps[] = [
    {
      title: t('common.footer.aboutUs'),
      links: [
        { label: t('common.footer.ceoMessage'), to: '/about#ceo-message' },
        { label: t('common.footer.companyVision'), to: '/about#vision' },
        { label: t('common.footer.companyProfile'), to: '/about#profile' },
        { label: t('common.footer.members'), to: '/about#members' },
      ],
    },
    {
      title: t('common.footer.services'),
      links: [
        { label: 'Bilionpass Travels', to: '/travel' },
        { label: 'Bilionpass Shops', to: '/shop' },
        { label: t('common.footer.marketing'), to: getLumivivUrl(i18n.resolvedLanguage), external: true },
        { label: t('common.footer.hr'), to: 'https://www.vistapath.co.jp', external: true },
      ],
    },
    {
      title: t('common.footer.news'),
      links: [{ label: t('common.footer.companyNews'), to: '/news' }],
    },
    {
      title: t('common.footer.recruitment'),
      links: [{ label: t('common.footer.joinUs'), to: '/recruit' }],
    },
    {
      title: t('common.footer.contactUs'),
      links: [{ label: t('common.footer.businessInquiry'), to: '/contact' }],
    },
  ];

  return (
    <footer className="flex min-w-[1480px] w-full flex-col items-start overflow-hidden bg-white px-0 pb-[48px] pt-[80px]">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-[90px]">
        <Link
          to="/"
          className="flex h-[70px] w-[274px] shrink-0 items-center"
          aria-label={t('common.accessibility.home')}
        >
          <img src={logo} alt="BilionPass" className="h-auto w-[274.4px] object-contain" />
        </Link>

        <div className="flex w-full items-start gap-[200px]">
          <div className="flex shrink-0 flex-col items-start gap-[24px]">
            <div className="text-[18px] font-medium leading-[27px] text-[#114861]">
              <p>{t('common.footer.travelLicense')}</p>
              <p>{t('common.footer.licenseNumber')}</p>
            </div>
            <address className="flex flex-col items-start gap-[16px] text-[16px] font-normal not-italic leading-[25.6px] text-[#355f72]">
              <p>{t('common.footer.address')}</p>
              <a href="tel:+81362057178" className="transition-colors hover:text-[#00a0e9]">
                TEL： 03-6205-7178
              </a>
              <a href="mailto:info@jdxjp.net" className="transition-colors hover:text-[#00a0e9]">
                Mail： info@jdxjp.net
              </a>
            </address>
          </div>

          <div className="flex items-start justify-between flex-1 min-w-0 gap-[24px]">
            {columns.map((column) => (
              <FooterColumn key={column.title} {...column} />
            ))}
          </div>
        </div>

        <p className="w-full text-center text-[16px] font-normal leading-[24px] text-[#666] opacity-50">
          © Japan DX All Rights Reserved.
        </p>
      </div>
    </footer>
  );
}
